/**
 * Infrastructure Page
 *
 * System health, database pool status, cron jobs, and LeakHunter metrics.
 * Technical monitoring for the platform.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  ServerIcon,
  CircleStackIcon,
  ClockIcon,
  BugAntIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function InfrastructurePage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getInfrastructure();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[InfrastructurePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 30 * 1000); // Refresh every 30 seconds for infra
    return () => clearInterval(interval);
  }, [currentEnv]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'failure':
      case 'timeout':
        return 'text-red-600 bg-red-100';
      case 'force_unlocked':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading infrastructure data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const database = data?.database || {};
  const apiHealth = data?.apiHealth || {};
  const cronJobs = data?.cronJobs || {};
  const leakHunter = data?.leakHunter || {};
  const recentAnalyses = data?.recentAnalyses || [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Infrastructure</h2>
          <p className="text-gray-500 mt-1">
            System health, database, and cron job monitoring
          </p>
        </div>
        <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
      </div>

      {/* Database Pool Health */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CircleStackIcon className="w-6 h-6 text-blue-500" />
          Database Pool
        </h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 animate-pulse bg-gray-100 rounded"></div>
              ))}
            </div>
          ) : database.poolHealth ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{database.poolHealth.total || 0}</p>
                <p className="text-sm text-gray-500">Total Connections</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{database.poolHealth.idle || 0}</p>
                <p className="text-sm text-gray-500">Idle</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{database.poolHealth.waiting || 0}</p>
                <p className="text-sm text-gray-500">Waiting</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">
                  {((database.poolHealth.total - database.poolHealth.idle) / (database.poolHealth.total || 1) * 100).toFixed(0)}%
                </p>
                <p className="text-sm text-gray-500">Utilization</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Pool health data unavailable</p>
          )}
        </div>
      </section>

      {/* API Health */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <ServerIcon className="w-6 h-6 text-green-500" />
          API Health (Last 24h)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title="Error Rate"
            value={apiHealth.errorRate}
            format="percent"
            loading={loading}
          />
          <KPICard
            title="Errors"
            value={apiHealth.errorsLast24h}
            loading={loading}
          />
          <KPICard
            title="Total Requests"
            value={apiHealth.totalRequestsLast24h}
            loading={loading}
          />
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h4 className="text-sm text-gray-500 uppercase tracking-wide">Latency</h4>
            {loading ? (
              <div className="h-12 mt-2 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">P50:</span>
                  <span className="font-medium">{apiHealth.latency?.p50Ms || '-'}ms</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">P95:</span>
                  <span className="font-medium">{apiHealth.latency?.p95Ms || '-'}ms</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">P99:</span>
                  <span className="font-medium">{apiHealth.latency?.p99Ms || '-'}ms</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cron Jobs */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <ClockIcon className="w-6 h-6 text-purple-500" />
          Cron Jobs
        </h3>

        {/* Cron Status Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <span className="text-gray-700">Healthy</span>
            </div>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {loading ? '-' : cronJobs.status?.healthy || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <XCircleIcon className="w-5 h-5 text-red-500" />
              <span className="text-gray-700">Failed</span>
            </div>
            <p className="text-3xl font-bold text-red-600 mt-2">
              {loading ? '-' : cronJobs.status?.failed || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
              <span className="text-gray-700">Force Unlocked</span>
            </div>
            <p className="text-3xl font-bold text-yellow-600 mt-2">
              {loading ? '-' : cronJobs.status?.forceUnlocked || 0}
            </p>
          </div>
        </div>

        {/* Recent Cron Jobs Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h4 className="font-medium text-gray-700">Recent Executions</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Executed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4">
                      <div className="h-8 animate-pulse bg-gray-100 rounded"></div>
                    </td>
                  </tr>
                ) : (cronJobs.recent || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No recent cron job executions
                    </td>
                  </tr>
                ) : (
                  (cronJobs.recent || []).map((job, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{job.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {job.durationMs}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {job.executedAt ? new Date(job.executedAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* LeakHunter */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BugAntIcon className="w-6 h-6 text-orange-500" />
          LeakHunter
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <KPICard
            title="Analyses Run (7d)"
            value={leakHunter.analysesRun}
            loading={loading}
          />
          <KPICard
            title="Patterns Detected"
            value={leakHunter.patternsDetected}
            loading={loading}
          />
          <KPICard
            title="Critical Issues"
            value={leakHunter.criticalIssues}
            loading={loading}
          />
        </div>

        {/* Recent Analyses */}
        {recentAnalyses.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h4 className="font-medium text-gray-700">Recent Analyses</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patterns
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentAnalyses.map((analysis) => (
                    <tr key={analysis.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-xs text-gray-600">
                          {analysis.id?.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {analysis.patternsDetected || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getSeverityColor(analysis.severity)}`}>
                          {analysis.severity || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {analysis.createdAt ? new Date(analysis.createdAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
